import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';
import { agentRoleSchema } from './hierarchy';

export const CURSOR_PROVIDER = 'cursor';
export const CURSOR_DEFAULT_MODEL = 'composer-2.5';
export const CURSOR_DEFAULT_MODEL_ID = `${CURSOR_PROVIDER}:${CURSOR_DEFAULT_MODEL}`;
export const cursorEfforts = ['low', 'medium', 'high', 'xhigh'] as const;
export const cursorEffortSchema = z.enum(cursorEfforts);
export type CursorEffort = z.infer<typeof cursorEffortSchema>;

export const modelConfigSchema = z.object({
  /** Vendor-agnostic model id. Agent execution uses cursor:<id>. */
  model: z.string().min(1).default(CURSOR_DEFAULT_MODEL_ID),
  temperature: z.number().min(0).max(2).default(0.2),
  maxOutputTokens: z.number().int().min(1).max(200_000).default(4096),
  /** Optional ordered fallbacks for provider outages. */
  fallbacks: z.array(z.string()).default([]),
  /** Cursor Cloud Agent reasoning effort. Ignored when `params` is set. */
  effort: cursorEffortSchema.optional(),
  /** Selected catalog level id (from Cursor.models.list). */
  levelId: z.string().min(1).optional(),
  /** Catalog params for the selected level. Preferred over hardcoded effort maps. */
  params: z.array(z.object({ id: z.string().min(1), value: z.string().min(1) })).optional(),
});
export type ModelConfig = z.infer<typeof modelConfigSchema>;

export class CursorChatCompletionsError extends Error {
  readonly code = 'CURSOR_NOT_CHAT_COMPLETIONS';
  constructor(modelId: string) {
    super(
      `Model "${modelId}" is a Cursor Cloud Agent backend and cannot be used with OpenAI/Anthropic/DeepSeek chat completions. Use a durable Collaborate run instead.`,
    );
    this.name = 'CursorChatCompletionsError';
  }
}

export class NonCursorAgentModelError extends Error {
  readonly code = 'CURSOR_AGENT_MODEL_REQUIRED';
  constructor(modelId: string) {
    super(`Agent model must be a Cursor Cloud Agent id (cursor:...). Got "${modelId}".`);
    this.name = 'NonCursorAgentModelError';
  }
}

export function splitProviderModelId(modelId: string): { provider: string; model: string } {
  const raw = String(modelId || '').trim();
  const idx = raw.indexOf(':');
  if (idx === -1) return { provider: 'openai', model: raw };
  return { provider: raw.slice(0, idx).toLowerCase(), model: raw.slice(idx + 1) };
}

export function isCursorModel(modelId: string | undefined | null): boolean {
  if (!modelId) return false;
  return splitProviderModelId(modelId).provider === CURSOR_PROVIDER;
}

export function cursorModelName(modelId: string): string {
  const { provider, model } = splitProviderModelId(modelId);
  if (provider !== CURSOR_PROVIDER) return CURSOR_DEFAULT_MODEL;
  return model.trim() || CURSOR_DEFAULT_MODEL;
}

export function assertNotCursorChatModel(modelId: string): void {
  if (isCursorModel(modelId)) throw new CursorChatCompletionsError(modelId);
}

export function assertCursorAgentModel(modelId: string): void {
  if (!isCursorModel(modelId)) throw new NonCursorAgentModelError(modelId);
}

export function coerceCursorAgentModel(modelId: string | undefined | null): string {
  const raw = String(modelId || '').trim();
  if (isCursorModel(raw)) return raw;
  return CURSOR_DEFAULT_MODEL_ID;
}

export function cursorAgentModelConfig(partial?: Partial<ModelConfig>): ModelConfig {
  const merged = modelConfigSchema.parse({
    ...partial,
    model: coerceCursorAgentModel(partial?.model),
  });
  assertCursorAgentModel(merged.model);
  return merged;
}

export function toCursorModelParams(
  modelId: string,
  effort: CursorEffort | undefined,
  params?: Array<{ id: string; value: string }>,
): Array<{ id: string; value: string }> {
  if (Array.isArray(params) && params.length > 0) {
    return params.filter((param) => param.id && param.value);
  }
  if (!effort) return [];
  const model = cursorModelName(modelId);
  if (model === 'gpt-5.5') {
    return [
      { id: 'context', value: '1m' },
      { id: 'reasoning', value: effort === 'xhigh' ? 'extra-high' : effort },
      { id: 'fast', value: 'false' },
    ];
  }
  if (model === 'claude-opus-4-7') {
    return [
      { id: 'thinking', value: 'true' },
      { id: 'context', value: '1m' },
      { id: 'effort', value: effort },
    ];
  }
  if (model === 'composer-2.5') {
    return [{ id: 'fast', value: effort === 'low' ? 'true' : 'false' }];
  }
  if (model === 'grok-4.6') {
    return [
      { id: 'effort', value: effort },
      { id: 'fast', value: 'false' },
    ];
  }
  return [];
}

export function buildCursorRuntimeSignature(input: {
  model: string;
  effort?: string;
  levelId?: string;
  params?: Array<{ id: string; value: string }>;
  mcpServers: unknown[];
}): string {
  const servers = [...(input.mcpServers || [])]
    .map((server) => normalizeCursorSignature(server))
    .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  const params = [...(input.params || [])]
    .map((param) => ({ id: String(param.id || ''), value: String(param.value || '') }))
    .sort((a, b) => `${a.id}:${a.value}`.localeCompare(`${b.id}:${b.value}`));
  return JSON.stringify({
    model: String(input.model || '').trim(),
    effort: String(input.effort || '').trim(),
    levelId: String(input.levelId || '').trim(),
    params,
    mcpServers: servers,
  });
}

function normalizeCursorSignature(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeCursorSignature);
  if (!value || typeof value !== 'object') return value;
  const obj = value as Record<string, unknown>;
  return Object.keys(obj)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      const raw = key.toLowerCase() === 'x-api-key' ? '<redacted>' : obj[key];
      acc[key] = normalizeCursorSignature(raw);
      return acc;
    }, {});
}

export const guardrailsSchema = z.object({
  piiRedaction: z.boolean().default(true),
  promptInjectionDefense: z.boolean().default(true),
  maxToolCallsPerRun: z.number().int().min(1).max(200).default(40),
  blockedToolKeys: z.array(z.string()).default([]),
});
export type Guardrails = z.infer<typeof guardrailsSchema>;

export const budgetSchema = z.object({
  /** Hard cap in USD cents per run. 0 = inherit tenant budget. */
  perRunUsdCents: z.number().int().min(0).default(0),
  /** Hard cap in USD cents per calendar month. */
  monthlyUsdCents: z.number().int().min(0).default(0),
});
export type Budget = z.infer<typeof budgetSchema>;

export const skillSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    key: z.string().min(1).max(120),
    name: z.string().min(1).max(200),
    description: z.string().max(2000).default(''),
    /** Prompt fragment injected into the agent system prompt. */
    instructions: z.string().max(20_000).default(''),
    /** Few-shot examples: arbitrary input/output pairs. */
    examples: z
      .array(z.object({ input: z.string(), output: z.string() }))
      .default([]),
  })
  .merge(timestampsSchema);
export type Skill = z.infer<typeof skillSchema>;

export const createSkillSchema = skillSchema.pick({
  key: true,
  name: true,
  description: true,
  instructions: true,
  examples: true,
}).extend({
  workspaceId: idSchema.optional(),
});
export type CreateSkill = z.infer<typeof createSkillSchema>;
export const updateSkillSchema = createSkillSchema.partial();
export type UpdateSkill = z.infer<typeof updateSkillSchema>;

export const agentStatuses = ['draft', 'active', 'paused', 'archived'] as const;
export const agentStatusSchema = z.enum(agentStatuses);
export type AgentStatus = z.infer<typeof agentStatusSchema>;

export const agentSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    /** Human role title, e.g. "Support Engineer", "SDR". */
    name: z.string().min(1).max(200),
    /** The job this role performs. */
    jobDescription: z.string().max(8000).default(''),
    systemPrompt: z.string().max(20_000).default(''),
    status: agentStatusSchema.default('draft'),
    /** Org tier (1 = orchestrator/top). */
    tier: z.number().int().min(1).max(10).default(1),
    /** Role in the agent network. */
    agentRole: agentRoleSchema.default('specialist'),
    model: modelConfigSchema,
    guardrails: guardrailsSchema,
    budget: budgetSchema,
    /** Default knowledge bases attached for RAG. */
    knowledgeBaseIds: z.array(idSchema).default([]),
    sync: z
      .object({
        status: z.enum(['in_sync', 'pending_sync', 'error']),
        lastSyncedAt: z.string().nullable().optional(),
        lastError: z.string().nullable().optional(),
      })
      .optional(),
  })
  .merge(timestampsSchema);
export type Agent = z.infer<typeof agentSchema>;

export const createAgentSchema = agentSchema
  .pick({
    name: true,
    jobDescription: true,
    systemPrompt: true,
    status: true,
    tier: true,
    agentRole: true,
    knowledgeBaseIds: true,
  })
  .extend({
    guardrails: guardrailsSchema.partial().optional(),
    budget: budgetSchema.partial().optional(),
  });
export type CreateAgent = z.infer<typeof createAgentSchema>;

export const updateAgentSchema = createAgentSchema.partial().extend({
  model: modelConfigSchema.partial().optional(),
});
export type UpdateAgent = z.infer<typeof updateAgentSchema>;

export const assignToolSchema = z.object({
  toolId: idSchema,
  /** Per-assignment override of approval requirement. */
  requiresApproval: z.boolean().optional(),
});
export type AssignTool = z.infer<typeof assignToolSchema>;

export const assignMcpServerSchema = z.object({
  mcpServerId: idSchema,
});
export type AssignMcpServer = z.infer<typeof assignMcpServerSchema>;
